/**
 * AppPermissionsDetailDrawer Component
 *
 * Right-sliding detail panel showing full OAuth-app details, AI assessment,
 * acknowledgment workflow, credential hygiene, app governance, directory
 * roles, ownership, and permission groups. Restyled to the editorial palette
 * (paper / navy / cream + sand-deep / blue-deep / green-deep / red-deep
 * semantic accents) to match the surrounding Security Center / App Posture
 * surface — same shell language as `<DriftDetailDrawer>`.
 */

import { useState, type ReactNode } from 'react'
import {
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  SparklesIcon,
  LightBulbIcon,
  CheckBadgeIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
  ShieldExclamationIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline'
import { AppPermissionsRiskGauge } from './AppPermissionsRiskGauge'
import type { OAuthAppDetail, OAuthAppPermission, AcknowledgeOAuthAppRequest } from './appPermissionsTypes'

interface AppPermissionsDetailDrawerProps {
  app: OAuthAppDetail | null
  isOpen: boolean
  isLoading: boolean
  onClose: () => void
  /** Called when an acknowledgment is submitted; parent handles the API call and data refresh */
  onAcknowledge?: (appId: string, request: AcknowledgeOAuthAppRequest) => Promise<void>
}

/* ────────────────────────────────────────────────────────────────────
   Editorial palette tone mappings. Critical / High / Medium / Low
   map to red-deep / sand-deep / blue-deep / green-deep so the OAuth
   surface reads of a piece with the drift queue's severity treatment.
   ──────────────────────────────────────────────────────────────────── */

interface ToneStyle {
  /** Filled chip background tint (rgba) */
  bg: string
  /** Chip border colour (token reference) */
  border: string
  /** Chip text colour (token reference) */
  text: string
}

const TIER_TONE: Record<string, ToneStyle> = {
  Critical: {
    bg: 'rgba(229,115,115,0.12)',
    border: 'var(--color-red-deep, #C84B4B)',
    text: 'var(--color-red-deep, #C84B4B)',
  },
  High: {
    bg: 'rgba(235,212,180,0.22)',
    border: 'var(--color-sand-deep, #C9AD80)',
    text: 'var(--color-sand-deep, #C9AD80)',
  },
  Medium: {
    bg: 'rgba(167,211,240,0.22)',
    border: 'var(--color-blue-deep, #7BA8C4)',
    text: 'var(--color-blue-deep, #7BA8C4)',
  },
  Low: {
    bg: 'rgba(118,214,157,0.16)',
    border: 'var(--color-green-deep, #4A9B6E)',
    text: 'var(--color-green-deep, #4A9B6E)',
  },
}

const ACTION_TONE: Record<string, ToneStyle> = {
  REVIEW: TIER_TONE.High,
  REDUCE: TIER_TONE.High,
  REVOKE: TIER_TONE.Critical,
  ACCEPT: TIER_TONE.Low,
}

const ACK_STATUS_TONE: Record<string, ToneStyle> = {
  Approved: TIER_TONE.Low,
  Investigating: TIER_TONE.High,
  Flagged: TIER_TONE.Critical,
}

/** Roles considered critical severity when assigned to a service principal */
const CRITICAL_ROLES = [
  'Global Administrator',
  'Privileged Role Administrator',
  'Security Administrator',
  'Exchange Administrator',
  'SharePoint Administrator',
  'Intune Administrator',
  'Application Administrator',
  'Cloud Application Administrator',
]

/** Roles considered high severity */
const HIGH_ROLES = [
  'User Administrator',
  'Groups Administrator',
  'Authentication Administrator',
  'Conditional Access Administrator',
  'Compliance Administrator',
]

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '--'
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function daysUntil(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null
  const diff = new Date(dateStr).getTime() - Date.now()
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

function getDirectoryRoleSeverity(role: string): 'Critical' | 'High' | 'Medium' {
  if (CRITICAL_ROLES.some(r => role.includes(r))) return 'Critical'
  if (HIGH_ROLES.some(r => role.includes(r))) return 'High'
  return 'Medium'
}

export function AppPermissionsDetailDrawer({ app, isOpen, isLoading, onClose, onAcknowledge }: AppPermissionsDetailDrawerProps) {
  const [appPermsOpen, setAppPermsOpen] = useState(true)
  const [delegatedPermsOpen, setDelegatedPermsOpen] = useState(true)

  return (
    <>
      {/* Backdrop — navy/30 + blur, matches DriftDetailDrawer so the cream
          page underneath reads through. */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-[color:var(--color-navy,#212938)]/30 backdrop-blur-sm transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed inset-y-0 right-0 w-full max-w-lg z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div
          className="flex h-full flex-col border-l bg-[color:var(--color-paper,#fff)] shadow-[0_1px_2px_rgba(33,41,56,0.06),0_24px_60px_-10px_rgba(33,41,56,0.18)]"
          style={{ borderColor: 'var(--color-rule, #E6E1D5)' }}
        >
          {/* Sticky header — quiet eyebrow + byline rail */}
          <div
            className="sticky top-0 z-10 flex items-center justify-between border-b bg-[color:var(--color-paper,#fff)]/95 px-7 py-4 backdrop-blur-sm"
            style={{ borderColor: 'var(--color-rule, #E6E1D5)' }}
          >
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[color:var(--color-mute,#6B7280)]">
                OAuth app · detail
              </div>
              <div className="mt-0.5 text-[11px] font-semibold text-[color:var(--color-faint,#9CA3AF)]">
                By Guardian
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 rounded-full p-2 text-[color:var(--color-mute,#6B7280)] transition-colors hover:bg-[color:var(--color-paper-warm,#FBF8F2)] hover:text-[color:var(--color-navy,#212938)]"
              aria-label="Close drawer"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 space-y-7 overflow-y-auto px-7 py-6">
            {isLoading ? (
              <DrawerSkeleton />
            ) : app ? (
              <>
                {/* ============================================================
                    App identity — risk tier pill + verified badge + italic-
                    mixed headline carrying the display name.
                    ============================================================ */}
                <div>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <TierPill tier={app.overallRiskTier} />
                    {app.publisherName && (
                      <span
                        className="font-mono text-[11px] font-semibold tabular-nums"
                        style={{ color: 'var(--color-faint, #9CA3AF)' }}
                      >
                        {app.publisherName}
                      </span>
                    )}
                    {app.isVerifiedPublisher && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.16em]"
                        style={{ color: 'var(--color-blue-deep, #7BA8C4)' }}
                        title="Verified Publisher"
                      >
                        <CheckBadgeIcon className="h-3.5 w-3.5" />
                        Verified
                      </span>
                    )}
                  </div>
                  <h3
                    className="mt-3 font-display text-[22px] font-extrabold leading-[1.2] tracking-[-0.018em]"
                    style={{ color: 'var(--color-navy, #212938)' }}
                  >
                    {app.displayName}
                  </h3>

                  {/* Metadata pills — quieter than tier; use rule-soft chips */}
                  {(app.isFirstParty || app.isMultiTenant || !app.accountEnabled || app.isStale) && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {app.isFirstParty && <MetaPill>1st Party</MetaPill>}
                      {app.isMultiTenant && <MetaPill>Multi-Tenant</MetaPill>}
                      {!app.accountEnabled && <MetaPill tone="Critical">Disabled</MetaPill>}
                      {app.isStale && <MetaPill>Stale</MetaPill>}
                    </div>
                  )}

                  {/* "Source of truth" affordance — opens the Entra Managed
                      App blade for this service principal in a new tab. */}
                  <a
                    href={entraAppUrl(app.servicePrincipalId, app.appId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.16em] transition-colors hover:opacity-80"
                    style={{ color: 'var(--color-blue-deep, #7BA8C4)' }}
                  >
                    View in Entra
                    <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                  </a>
                </div>

                {/* ============================================================
                    AI Assessment — paper-warm card with editorial chrome
                    ============================================================ */}
                {(app.aiRiskScore !== null || app.aiRiskSummary) && (
                  <>
                    <SectionRule />
                    <section>
                      <DrawerSectionHeader
                        eyebrow="Section · 01 · Guardian's read"
                        headline={
                          <>
                            Reading the <em>signal.</em>
                          </>
                        }
                        emColor="var(--color-blue-deep, #7BA8C4)"
                        byline="By Guardian"
                        icon={<SparklesIcon className="h-3.5 w-3.5" />}
                      />
                      <div
                        className="mt-4 rounded-[10px] border p-5 space-y-4"
                        style={{
                          background: 'var(--color-paper-warm, #FBF8F2)',
                          borderColor: 'var(--color-rule-soft, #F0EBDD)',
                        }}
                      >
                        <AppPermissionsRiskGauge score={app.aiRiskScore} tier={app.overallRiskTier} />

                        {app.aiRiskSummary && (
                          <p
                            className="text-sm leading-[1.6]"
                            style={{ color: 'var(--color-navy, #212938)' }}
                          >
                            {app.aiRiskSummary}
                          </p>
                        )}

                        {app.aiRecommendation && (
                          <div
                            className="flex items-start gap-2.5 rounded-[8px] border-l-[3px] px-3 py-2.5"
                            style={{
                              borderLeftColor: 'var(--color-sand-deep, #C9AD80)',
                              background: 'var(--color-paper, #fff)',
                              borderTop: '1px solid var(--color-rule-soft, #F0EBDD)',
                              borderRight: '1px solid var(--color-rule-soft, #F0EBDD)',
                              borderBottom: '1px solid var(--color-rule-soft, #F0EBDD)',
                            }}
                          >
                            <LightBulbIcon
                              className="h-4 w-4 flex-shrink-0 mt-0.5"
                              style={{ color: 'var(--color-sand-deep, #C9AD80)' }}
                            />
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)]">
                                  Recommendation
                                </span>
                                {app.aiRecommendationAction && (
                                  <ActionPill action={app.aiRecommendationAction} />
                                )}
                              </div>
                              <p
                                className="text-[13px] leading-[1.55]"
                                style={{ color: 'var(--color-navy, #212938)' }}
                              >
                                {app.aiRecommendation}
                              </p>
                            </div>
                          </div>
                        )}

                        {app.aiEnrichedAt && (
                          <p
                            className="font-mono text-[10px] font-semibold tabular-nums"
                            style={{ color: 'var(--color-faint, #9CA3AF)' }}
                          >
                            Analyzed {formatDate(app.aiEnrichedAt)}
                          </p>
                        )}
                      </div>
                    </section>
                  </>
                )}

                {/* ============================================================
                    App Status — acknowledgment workflow
                    ============================================================ */}
                <SectionRule />
                <AcknowledgmentSection app={app} onAcknowledge={onAcknowledge} />

                {/* ============================================================
                    Credential Hygiene
                    ============================================================ */}
                <SectionRule />
                <CredentialHygieneSection app={app} />

                {/* ============================================================
                    App Governance
                    ============================================================ */}
                <SectionRule />
                <section>
                  <DrawerSectionHeader
                    eyebrow="Section · Governance"
                    headline={
                      <>
                        How it was <em>granted.</em>
                      </>
                    }
                    emColor="var(--color-sand-deep, #C9AD80)"
                  />
                  <div className="mt-4 grid grid-cols-2 gap-2.5">
                    <DetailItem label="Consent Type" value={app.consentType || '--'} />
                    <DetailItem label="App Created" value={formatDate(app.createdDateTime)} />
                    <div
                      className="col-span-2 rounded-[8px] border px-3 py-2.5"
                      style={{
                        background: 'var(--color-paper-warm, #FBF8F2)',
                        borderColor: 'var(--color-rule-soft, #F0EBDD)',
                      }}
                    >
                      <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)] mb-1">
                        App Scope
                      </span>
                      {app.appRoleAssignmentRequired ? (
                        <SemanticPill tone="Low">Assigned Users Only</SemanticPill>
                      ) : (
                        <SemanticPill tone="High">Org-wide</SemanticPill>
                      )}
                    </div>
                  </div>
                </section>

                {/* ============================================================
                    Directory Roles — only shown when roles exist
                    ============================================================ */}
                {app.directoryRoles && app.directoryRoles.length > 0 && (
                  <>
                    <SectionRule />
                    <section>
                      <DrawerSectionHeader
                        eyebrow="Section · Directory roles"
                        headline={
                          <>
                            Privileged <em>assignments.</em>
                          </>
                        }
                        emColor="var(--color-red-deep, #C84B4B)"
                        icon={<ShieldExclamationIcon className="h-3.5 w-3.5" />}
                      />
                      <ul className="mt-4 space-y-1.5">
                        {app.directoryRoles.map(role => {
                          const severity = getDirectoryRoleSeverity(role)
                          const tone = TIER_TONE[severity]
                          return (
                            <li
                              key={role}
                              className="flex items-center justify-between rounded-[8px] border-l-[3px] px-3 py-2"
                              style={{
                                borderLeftColor: tone.text,
                                background: 'var(--color-paper-warm, #FBF8F2)',
                                borderTop: '1px solid var(--color-rule-soft, #F0EBDD)',
                                borderRight: '1px solid var(--color-rule-soft, #F0EBDD)',
                                borderBottom: '1px solid var(--color-rule-soft, #F0EBDD)',
                              }}
                            >
                              <span
                                className="text-[13px] font-semibold"
                                style={{ color: 'var(--color-navy, #212938)' }}
                              >
                                {role}
                              </span>
                              <SemanticPill tone={severity}>{severity}</SemanticPill>
                            </li>
                          )
                        })}
                      </ul>
                    </section>
                  </>
                )}

                {/* ============================================================
                    App Ownership
                    ============================================================ */}
                <SectionRule />
                <section>
                  <DrawerSectionHeader
                    eyebrow="Section · Ownership"
                    headline={
                      <>
                        Who's <em>accountable.</em>
                      </>
                    }
                    emColor="var(--color-blue-deep, #7BA8C4)"
                    icon={<UserGroupIcon className="h-3.5 w-3.5" />}
                  />
                  {app.owners && app.owners.length > 0 ? (
                    <ul className="mt-4 space-y-1.5">
                      {app.owners.map(owner => (
                        <li
                          key={owner}
                          className="rounded-[8px] border px-3 py-2 text-[13px] font-semibold"
                          style={{
                            borderColor: 'var(--color-rule-soft, #F0EBDD)',
                            background: 'var(--color-paper-warm, #FBF8F2)',
                            color: 'var(--color-navy, #212938)',
                          }}
                        >
                          {owner}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <NoOwnersNotice servicePrincipalId={app.servicePrincipalId} appId={app.appId} />
                  )}
                </section>

                {/* ============================================================
                    Permissions — application + delegated
                    ============================================================ */}
                <SectionRule />
                <section className="space-y-3">
                  <DrawerSectionHeader
                    eyebrow="Section · Permissions"
                    headline={
                      <>
                        What it <em>can do.</em>
                      </>
                    }
                    emColor="var(--color-sand-deep, #C9AD80)"
                  />
                  <PermissionGroup
                    title="Application Permissions"
                    permissions={app.applicationPermissions}
                    isOpen={appPermsOpen}
                    onToggle={() => setAppPermsOpen(prev => !prev)}
                  />
                  <PermissionGroup
                    title="Delegated Permissions"
                    permissions={app.delegatedPermissions}
                    isOpen={delegatedPermsOpen}
                    onToggle={() => setDelegatedPermsOpen(prev => !prev)}
                  />
                </section>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </>
  )
}

/* ────────────────────────────────────────────────────────────────────
   Drawer-local presentational atoms
   ──────────────────────────────────────────────────────────────────── */

/** SectionRule — double-rule visual break between drawer sections. */
function SectionRule() {
  return (
    <div
      aria-hidden
      className="h-[4px]"
      style={{
        borderTop: '1px solid var(--color-rule, #E6E1D5)',
        borderBottom: '1px solid var(--color-rule, #E6E1D5)',
      }}
    />
  )
}

/** DrawerSectionHeader — eyebrow + italic-mixed headline + optional byline. */
function DrawerSectionHeader({
  eyebrow,
  headline,
  emColor,
  byline,
  icon,
}: {
  eyebrow: string
  headline: ReactNode
  emColor: string
  byline?: string
  icon?: ReactNode
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-[color:var(--color-mute,#6B7280)]">
        {icon && <span style={{ color: emColor }}>{icon}</span>}
        {eyebrow}
      </div>
      <h4
        className="mt-1.5 font-display text-[18px] font-extrabold leading-[1.22] tracking-[-0.014em]"
        style={{ color: 'var(--color-navy, #212938)' }}
      >
        <span
          style={{ ['--em-color' as string]: emColor }}
          className="[&_em]:font-serif [&_em]:font-semibold [&_em]:italic [&_em]:[color:var(--em-color)]"
        >
          {headline}
        </span>
      </h4>
      {byline && (
        <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[color:var(--color-faint,#9CA3AF)]">
          {byline}
        </div>
      )}
    </div>
  )
}

/** TierPill — risk-tier badge with semantic accent. */
function TierPill({ tier }: { tier: string }) {
  const tone = TIER_TONE[tier] || {
    bg: 'rgba(107,114,128,0.10)',
    border: 'var(--color-mute, #6B7280)',
    text: 'var(--color-mute, #6B7280)',
  }
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]"
      style={{
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        color: tone.text,
      }}
    >
      {tier} Risk
    </span>
  )
}

/** SemanticPill — small tier-toned label used inside detail rows. */
function SemanticPill({
  tone,
  children,
}: {
  tone: 'Critical' | 'High' | 'Medium' | 'Low'
  children: ReactNode
}) {
  const t = TIER_TONE[tone]
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]"
      style={{
        background: t.bg,
        border: `1px solid ${t.border}`,
        color: t.text,
      }}
    >
      {children}
    </span>
  )
}

/** ActionPill — REVIEW / REDUCE / REVOKE / ACCEPT recommendation chip. */
function ActionPill({ action }: { action: string }) {
  const tone = ACTION_TONE[action] || {
    bg: 'rgba(107,114,128,0.10)',
    border: 'var(--color-mute, #6B7280)',
    text: 'var(--color-mute, #6B7280)',
  }
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.16em]"
      style={{
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        color: tone.text,
      }}
    >
      {action}
    </span>
  )
}

/** MetaPill — quieter chip for first-party / multi-tenant / disabled / stale */
function MetaPill({
  children,
  tone,
}: {
  children: ReactNode
  tone?: 'Critical' | 'High' | 'Medium' | 'Low'
}) {
  if (tone) {
    return <SemanticPill tone={tone}>{children}</SemanticPill>
  }
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]"
      style={{
        background: 'var(--color-paper-warm, #FBF8F2)',
        border: '1px solid var(--color-rule, #E6E1D5)',
        color: 'var(--color-mute, #6B7280)',
      }}
    >
      {children}
    </span>
  )
}

/** DetailItem — small key/value tile used in governance + credential grids. */
function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div
      className="rounded-[8px] border px-3 py-2.5"
      style={{
        background: 'var(--color-paper-warm, #FBF8F2)',
        borderColor: 'var(--color-rule-soft, #F0EBDD)',
      }}
    >
      <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)] mb-1">
        {label}
      </span>
      <span
        className="block truncate text-[13px] font-semibold"
        style={{ color: 'var(--color-navy, #212938)' }}
        title={value}
      >
        {value}
      </span>
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────
   No-owners notice with Entra deep link
   ──────────────────────────────────────────────────────────────────── */

/** Deep link into the Entra admin-centre Managed App blade for an enterprise
 *  application. `objectId` is the service-principal id; `appId` is the
 *  application (client) id. Both are required. `blade` chooses which tab the
 *  link lands on — Overview is the default; Owners is used by the no-owners
 *  notice below. */
function entraAppUrl(
  servicePrincipalId: string,
  appId: string,
  blade: 'Overview' | 'Owners' | 'Permissions' = 'Overview',
): string {
  return `https://entra.microsoft.com/#view/Microsoft_AAD_IAM/ManagedAppMenuBlade/~/${blade}/objectId/${encodeURIComponent(
    servicePrincipalId,
  )}/appId/${encodeURIComponent(appId)}`
}

function NoOwnersNotice({
  servicePrincipalId,
  appId,
}: {
  servicePrincipalId: string
  appId: string
}) {
  return (
    <div
      className="mt-4 rounded-[10px] border-l-[3px] p-4 space-y-3"
      style={{
        borderLeftColor: 'var(--color-sand-deep, #C9AD80)',
        background: 'rgba(235,212,180,0.18)',
        borderTop: '1px solid var(--color-rule-soft, #F0EBDD)',
        borderRight: '1px solid var(--color-rule-soft, #F0EBDD)',
        borderBottom: '1px solid var(--color-rule-soft, #F0EBDD)',
      }}
    >
      <div className="flex items-start gap-2">
        <ExclamationTriangleIcon
          className="h-4 w-4 flex-shrink-0 mt-0.5"
          style={{ color: 'var(--color-sand-deep, #C9AD80)' }}
        />
        <div className="min-w-0 flex-1">
          <p
            className="text-[13px] font-semibold leading-[1.45]"
            style={{ color: 'var(--color-navy, #212938)' }}
          >
            No owners assigned.
          </p>
          <p
            className="mt-1 text-[12px] leading-[1.55]"
            style={{ color: 'var(--color-mute, #6B7280)' }}
          >
            Assign at least one owner in Microsoft Entra so someone is accountable
            for renewals, consent reviews, and incident response. In the Entra
            admin centre, open the app's <em className="italic">Owners</em> blade
            and add a user or group.
          </p>
        </div>
      </div>
      <a
        href={entraAppUrl(servicePrincipalId, appId, 'Owners')}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] transition-colors hover:bg-[color:var(--color-paper,#fff)]"
        style={{
          borderColor: 'var(--color-navy, #212938)',
          background: 'var(--color-paper, #fff)',
          color: 'var(--color-navy, #212938)',
        }}
      >
        Assign owner in Entra
        <ArrowTopRightOnSquareIcon className="h-3 w-3" />
      </a>
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────
   Credential Hygiene
   ──────────────────────────────────────────────────────────────────── */

function CredentialHygieneSection({ app }: { app: OAuthAppDetail }) {
  const credentialType = app.credentialType || 'None'
  const expiryDays = daysUntil(app.earliestCredentialExpiry)
  const isExternal = app.isExternalApp && credentialType === 'None'

  const getAuthMethodTone = (): { label: string; suffix: string; color: string } => {
    if (isExternal) return { label: 'External', suffix: '', color: 'var(--color-mute, #6B7280)' }
    switch (credentialType) {
      case 'Certificate':
        return { label: 'Certificate', suffix: ' ✓', color: 'var(--color-green-deep, #4A9B6E)' }
      case 'Secret':
        return { label: 'Secret', suffix: '', color: 'var(--color-sand-deep, #C9AD80)' }
      case 'Both':
        return { label: 'Both', suffix: '', color: 'var(--color-sand-deep, #C9AD80)' }
      case 'FederatedIdentity':
        return { label: 'Federated', suffix: '', color: 'var(--color-green-deep, #4A9B6E)' }
      case 'None':
        return { label: 'None', suffix: '', color: 'var(--color-faint, #9CA3AF)' }
      default:
        return { label: credentialType, suffix: '', color: 'var(--color-mute, #6B7280)' }
    }
  }

  const getStatus = (): { label: string; dotColor: string; textColor: string } => {
    if (isExternal) {
      return {
        label: 'Managed by publisher',
        dotColor: 'var(--color-mute, #6B7280)',
        textColor: 'var(--color-mute, #6B7280)',
      }
    }
    if (credentialType === 'None') {
      return {
        label: 'No credentials configured',
        dotColor: 'var(--color-faint, #9CA3AF)',
        textColor: 'var(--color-faint, #9CA3AF)',
      }
    }
    if (app.hasExpiredCredentials) {
      return {
        label: 'Expired',
        dotColor: 'var(--color-red-deep, #C84B4B)',
        textColor: 'var(--color-red-deep, #C84B4B)',
      }
    }
    if (app.hasExpiringCredentials || (expiryDays !== null && expiryDays <= 30)) {
      return {
        label: 'Expiring',
        dotColor: 'var(--color-sand-deep, #C9AD80)',
        textColor: 'var(--color-sand-deep, #C9AD80)',
      }
    }
    return {
      label: 'All current',
      dotColor: 'var(--color-green-deep, #4A9B6E)',
      textColor: 'var(--color-green-deep, #4A9B6E)',
    }
  }

  const getExpiryColor = (): string => {
    if (expiryDays === null) return 'var(--color-mute, #6B7280)'
    if (expiryDays < 0) return 'var(--color-red-deep, #C84B4B)'
    if (expiryDays <= 30) return 'var(--color-sand-deep, #C9AD80)'
    return 'var(--color-green-deep, #4A9B6E)'
  }

  const authMethod = getAuthMethodTone()
  const status = getStatus()

  return (
    <section>
      <DrawerSectionHeader
        eyebrow="Section · Credential hygiene"
        headline={
          <>
            Keys &amp; <em>secrets.</em>
          </>
        }
        emColor="var(--color-sand-deep, #C9AD80)"
        icon={<KeyIcon className="h-3.5 w-3.5" />}
      />
      {isExternal ? (
        <div
          className="mt-4 rounded-[10px] border-l-[3px] px-4 py-3 space-y-2"
          style={{
            borderLeftColor: 'var(--color-mute, #6B7280)',
            background: 'var(--color-paper-warm, #FBF8F2)',
            borderTop: '1px solid var(--color-rule-soft, #F0EBDD)',
            borderRight: '1px solid var(--color-rule-soft, #F0EBDD)',
            borderBottom: '1px solid var(--color-rule-soft, #F0EBDD)',
          }}
        >
          <div className="flex items-center gap-1.5">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ background: 'var(--color-mute, #6B7280)' }}
            />
            <span
              className="text-[13px] font-semibold"
              style={{ color: 'var(--color-navy, #212938)' }}
            >
              Managed by publisher
            </span>
          </div>
          <p
            className="text-[12px] leading-[1.55]"
            style={{ color: 'var(--color-mute, #6B7280)' }}
          >
            This is a multi-tenant app registered in another organization. Credentials
            (certificates, secrets) are managed by the publisher in their home tenant
            and are not visible from this tenant.
          </p>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-2 gap-2.5">
          <CredCard label="Auth Method">
            <span className="block text-[13px] font-semibold" style={{ color: authMethod.color }}>
              {authMethod.label}
              {authMethod.suffix}
            </span>
          </CredCard>
          <CredCard label="Certificates">
            <span
              className="block text-[13px] font-semibold"
              style={{ color: 'var(--color-navy, #212938)' }}
            >
              {app.certificateCount} active
            </span>
          </CredCard>
          <CredCard label="Secrets">
            <span
              className="block text-[13px] font-semibold"
              style={{
                color:
                  app.secretCount > 0
                    ? 'var(--color-sand-deep, #C9AD80)'
                    : 'var(--color-navy, #212938)',
              }}
            >
              {app.secretCount}
            </span>
          </CredCard>
          {app.federatedCredentialCount > 0 && (
            <CredCard label="Federated">
              <span
                className="block text-[13px] font-semibold"
                style={{ color: 'var(--color-navy, #212938)' }}
              >
                {app.federatedCredentialCount}
              </span>
            </CredCard>
          )}
          {app.earliestCredentialExpiry && (
            <CredCard label="Next Expiry">
              <span className="block text-[13px] font-semibold" style={{ color: getExpiryColor() }}>
                {formatDate(app.earliestCredentialExpiry)}
                {expiryDays !== null && (
                  <span className="ml-1 font-mono text-[11px] tabular-nums">
                    ({expiryDays < 0 ? `${Math.abs(expiryDays)}d ago` : `${expiryDays}d`})
                  </span>
                )}
              </span>
            </CredCard>
          )}
          <CredCard label="Status">
            <span
              className="flex items-center gap-1.5 text-[13px] font-semibold"
              style={{ color: status.textColor }}
            >
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ background: status.dotColor }}
              />
              {status.label}
            </span>
          </CredCard>
        </div>
      )}
    </section>
  )
}

function CredCard({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div
      className="rounded-[8px] border px-3 py-2.5"
      style={{
        background: 'var(--color-paper-warm, #FBF8F2)',
        borderColor: 'var(--color-rule-soft, #F0EBDD)',
      }}
    >
      <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)] mb-1">
        {label}
      </span>
      {children}
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────
   Acknowledgment Section
   ──────────────────────────────────────────────────────────────────── */

const VERIFICATION_CHECKLIST = [
  'Verify app identity and publisher',
  'Confirm business purpose',
  'Review permissions vs stated purpose',
  'Check scoping (org-wide vs assigned users)',
  'Verify credential hygiene',
  'Assess multi-tenant risk',
  'Review sign-in activity',
]

function AcknowledgmentSection({
  app,
  onAcknowledge,
}: {
  app: OAuthAppDetail
  onAcknowledge?: (appId: string, request: AcknowledgeOAuthAppRequest) => Promise<void>
}) {
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [formStatus, setFormStatus] = useState<string>(app.acknowledgmentStatus || 'Approved')
  const [reason, setReason] = useState(app.acknowledgmentReason || '')
  const [expiryMonths, setExpiryMonths] = useState<string>('6')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [checkedItems, setCheckedItems] = useState<boolean[]>(new Array(VERIFICATION_CHECKLIST.length).fill(false))
  const [showChecklist, setShowChecklist] = useState(false)

  const isAcknowledged = !!app.acknowledgmentStatus
  const isExpired = app.acknowledgmentExpiresAt ? new Date(app.acknowledgmentExpiresAt) < new Date() : false
  const isHighRisk = app.overallRiskTier === 'Critical' || app.overallRiskTier === 'High'

  const handleSubmit = async () => {
    if (!reason.trim() || !onAcknowledge) return
    setIsSubmitting(true)
    try {
      await onAcknowledge(app.id, {
        status: formStatus,
        reason: reason.trim(),
        expiresInMonths: expiryMonths === 'none' ? undefined : parseInt(expiryMonths, 10),
      })
      setIsFormOpen(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  const toggleCheckItem = (index: number) => {
    setCheckedItems(prev => {
      const next = [...prev]
      next[index] = !next[index]
      return next
    })
  }

  return (
    <section>
      <DrawerSectionHeader
        eyebrow="Section · App status"
        headline={
          <>
            Where it <em>stands.</em>
          </>
        }
        emColor="var(--color-blue-deep, #7BA8C4)"
        icon={<ClipboardDocumentCheckIcon className="h-3.5 w-3.5" />}
      />

      <div className="mt-4">
        {isAcknowledged && !isFormOpen ? (
          /* Show current acknowledgment details */
          <div
            className="rounded-[10px] border p-4 space-y-3"
            style={{
              background: 'var(--color-paper-warm, #FBF8F2)',
              borderColor: 'var(--color-rule-soft, #F0EBDD)',
            }}
          >
            {isExpired && (
              <div
                className="flex items-center gap-2 rounded-[6px] border-l-[3px] px-3 py-2"
                style={{
                  borderLeftColor: 'var(--color-sand-deep, #C9AD80)',
                  background: 'rgba(235,212,180,0.18)',
                }}
              >
                <ExclamationTriangleIcon
                  className="h-4 w-4 flex-shrink-0"
                  style={{ color: 'var(--color-sand-deep, #C9AD80)' }}
                />
                <span
                  className="text-[12px] font-semibold"
                  style={{ color: 'var(--color-navy, #212938)' }}
                >
                  Review expired. Re-evaluate this app.
                </span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)] mb-1">
                  Status
                </span>
                {app.acknowledgmentStatus && ACK_STATUS_TONE[app.acknowledgmentStatus] ? (
                  <SemanticPill
                    tone={
                      app.acknowledgmentStatus === 'Flagged'
                        ? 'Critical'
                        : app.acknowledgmentStatus === 'Investigating'
                          ? 'High'
                          : 'Low'
                    }
                  >
                    {app.acknowledgmentStatus}
                  </SemanticPill>
                ) : (
                  <span
                    className="text-[13px] font-semibold"
                    style={{ color: 'var(--color-navy, #212938)' }}
                  >
                    {app.acknowledgmentStatus}
                  </span>
                )}
              </div>
              <div>
                <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)] mb-1">
                  Reviewed By
                </span>
                <span
                  className="block truncate text-[13px] font-semibold"
                  style={{ color: 'var(--color-navy, #212938)' }}
                >
                  {app.acknowledgedBy || '--'}
                </span>
              </div>
              <div>
                <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)] mb-1">
                  Reviewed At
                </span>
                <span
                  className="block text-[13px] font-semibold"
                  style={{ color: 'var(--color-navy, #212938)' }}
                >
                  {formatDate(app.acknowledgedAt)}
                </span>
              </div>
              <div>
                <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)] mb-1">
                  Review Expires
                </span>
                <span
                  className="block text-[13px] font-semibold"
                  style={{
                    color: isExpired
                      ? 'var(--color-sand-deep, #C9AD80)'
                      : 'var(--color-navy, #212938)',
                  }}
                >
                  {app.acknowledgmentExpiresAt ? formatDate(app.acknowledgmentExpiresAt) : 'No expiry'}
                </span>
              </div>
            </div>
            {app.acknowledgmentReason && (
              <div>
                <span className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)] mb-1">
                  Reason
                </span>
                <p
                  className="text-[12px] leading-[1.55]"
                  style={{ color: 'var(--color-navy, #212938)' }}
                >
                  {app.acknowledgmentReason}
                </p>
              </div>
            )}
            <button
              onClick={() => {
                setFormStatus(app.acknowledgmentStatus || 'Approved')
                setReason(app.acknowledgmentReason || '')
                setIsFormOpen(true)
              }}
              className="w-full rounded-full border px-3 py-2 text-[12px] font-bold uppercase tracking-[0.14em] transition-colors hover:bg-[color:var(--color-paper,#fff)]"
              style={{
                borderColor: 'var(--color-rule, #E6E1D5)',
                color: 'var(--color-navy, #212938)',
                background: 'var(--color-paper, #fff)',
              }}
            >
              Update review
            </button>
          </div>
        ) : !isFormOpen ? (
          <button
            onClick={() => setIsFormOpen(true)}
            className="flex w-full items-center justify-center gap-2 rounded-full border px-4 py-2.5 text-[12px] font-bold uppercase tracking-[0.14em] transition-colors"
            style={{
              borderColor: 'var(--color-navy, #212938)',
              background: 'var(--color-navy, #212938)',
              color: 'var(--color-paper, #fff)',
            }}
          >
            <ClipboardDocumentCheckIcon className="h-4 w-4" />
            Review app
          </button>
        ) : null}

        {/* Acknowledgment form */}
        {isFormOpen && (
          <div
            className="rounded-[10px] border p-4 space-y-4"
            style={{
              background: 'var(--color-paper-warm, #FBF8F2)',
              borderColor: 'var(--color-rule-soft, #F0EBDD)',
            }}
          >
            {/* Verification checklist for Critical/High risk apps */}
            {isHighRisk && (
              <div className="space-y-2">
                <button
                  onClick={() => setShowChecklist(prev => !prev)}
                  className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] transition-colors"
                  style={{ color: 'var(--color-sand-deep, #C9AD80)' }}
                >
                  {showChecklist ? (
                    <ChevronUpIcon className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronDownIcon className="h-3.5 w-3.5" />
                  )}
                  Verification checklist · {checkedItems.filter(Boolean).length}/{VERIFICATION_CHECKLIST.length}
                </button>
                {showChecklist && (
                  <div className="space-y-1.5 pl-1">
                    {VERIFICATION_CHECKLIST.map((item, i) => (
                      <label key={i} className="flex items-start gap-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={checkedItems[i]}
                          onChange={() => toggleCheckItem(i)}
                          className="mt-0.5 rounded"
                          style={{
                            accentColor: 'var(--color-navy, #212938)',
                          }}
                        />
                        <span
                          className="text-[12px] leading-[1.5]"
                          style={{
                            color: checkedItems[i]
                              ? 'var(--color-navy, #212938)'
                              : 'var(--color-mute, #6B7280)',
                          }}
                        >
                          {item}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Status dropdown */}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)] mb-1.5">
                Status
              </label>
              <select
                value={formStatus}
                onChange={e => setFormStatus(e.target.value)}
                className="w-full rounded-[8px] border bg-[color:var(--color-paper,#fff)] px-3 py-2 text-[13px] font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-[color:var(--color-navy,#212938)]/20"
                style={{
                  borderColor: 'var(--color-rule, #E6E1D5)',
                  color: 'var(--color-navy, #212938)',
                }}
              >
                <option value="Approved">Approved</option>
                <option value="Investigating">Investigating</option>
                <option value="Flagged">Flagged</option>
              </select>
            </div>

            {/* Reason */}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)] mb-1.5">
                Reason <span style={{ color: 'var(--color-red-deep, #C84B4B)' }}>*</span>
              </label>
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Describe the business justification or investigation notes..."
                rows={3}
                className="w-full resize-none rounded-[8px] border bg-[color:var(--color-paper,#fff)] px-3 py-2 text-[13px] leading-[1.55] transition-colors focus:outline-none focus:ring-2 focus:ring-[color:var(--color-navy,#212938)]/20"
                style={{
                  borderColor: 'var(--color-rule, #E6E1D5)',
                  color: 'var(--color-navy, #212938)',
                }}
              />
            </div>

            {/* Review period */}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)] mb-1.5">
                Review Period
              </label>
              <select
                value={expiryMonths}
                onChange={e => setExpiryMonths(e.target.value)}
                className="w-full rounded-[8px] border bg-[color:var(--color-paper,#fff)] px-3 py-2 text-[13px] font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-[color:var(--color-navy,#212938)]/20"
                style={{
                  borderColor: 'var(--color-rule, #E6E1D5)',
                  color: 'var(--color-navy, #212938)',
                }}
              >
                <option value="3">3 months</option>
                <option value="6">6 months</option>
                <option value="12">12 months</option>
                <option value="none">No expiry</option>
              </select>
            </div>

            {/* Submit / Cancel */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={handleSubmit}
                disabled={!reason.trim() || isSubmitting || !onAcknowledge}
                className="flex-1 rounded-full px-4 py-2 text-[12px] font-bold uppercase tracking-[0.14em] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  background: 'var(--color-navy, #212938)',
                  color: 'var(--color-paper, #fff)',
                }}
              >
                {isSubmitting ? 'Saving...' : 'Submit'}
              </button>
              <button
                onClick={() => setIsFormOpen(false)}
                className="rounded-full border px-4 py-2 text-[12px] font-bold uppercase tracking-[0.14em] transition-colors hover:bg-[color:var(--color-paper,#fff)]"
                style={{
                  borderColor: 'var(--color-rule, #E6E1D5)',
                  color: 'var(--color-navy, #212938)',
                  background: 'var(--color-paper, #fff)',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

/* ────────────────────────────────────────────────────────────────────
   Permission Group
   ──────────────────────────────────────────────────────────────────── */

interface PermissionGroupProps {
  title: string
  permissions: OAuthAppPermission[]
  isOpen: boolean
  onToggle: () => void
}

function PermissionGroup({ title, permissions, isOpen, onToggle }: PermissionGroupProps) {
  return (
    <div
      className="overflow-hidden rounded-[10px] border"
      style={{ borderColor: 'var(--color-rule-soft, #F0EBDD)' }}
    >
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-[color:var(--color-paper-warm,#FBF8F2)]"
        style={{ background: 'var(--color-paper, #fff)' }}
      >
        <div className="flex items-center gap-2">
          <span
            className="text-[13px] font-bold"
            style={{ color: 'var(--color-navy, #212938)' }}
          >
            {title}
          </span>
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums"
            style={{
              background: 'var(--color-paper-warm, #FBF8F2)',
              border: '1px solid var(--color-rule, #E6E1D5)',
              color: 'var(--color-mute, #6B7280)',
            }}
          >
            {permissions.length}
          </span>
        </div>
        <ChevronDownIcon
          className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          style={{ color: 'var(--color-mute, #6B7280)' }}
        />
      </button>

      {isOpen && (
        <div
          className="divide-y"
          style={{ borderColor: 'var(--color-rule-soft, #F0EBDD)' }}
        >
          {permissions.length === 0 ? (
            <div
              className="px-4 py-3 text-[12px] italic"
              style={{ color: 'var(--color-faint, #9CA3AF)' }}
            >
              No permissions of this type.
            </div>
          ) : (
            permissions.map(perm => (
              <div
                key={perm.id}
                className="px-4 py-3 transition-colors hover:bg-[color:var(--color-paper-warm,#FBF8F2)]"
                style={{ borderColor: 'var(--color-rule-soft, #F0EBDD)' }}
              >
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <code
                    className="font-mono text-[12px] font-semibold"
                    style={{ color: 'var(--color-navy, #212938)' }}
                  >
                    {perm.permissionName}
                  </code>
                  <SemanticPill
                    tone={
                      perm.riskTier === 'Critical'
                        ? 'Critical'
                        : perm.riskTier === 'High'
                          ? 'High'
                          : perm.riskTier === 'Medium'
                            ? 'Medium'
                            : 'Low'
                    }
                  >
                    {perm.riskTier}
                  </SemanticPill>
                </div>
                <p className="text-[11px]" style={{ color: 'var(--color-mute, #6B7280)' }}>
                  {perm.resourceDisplayName}
                </p>
                {perm.permissionDescription && (
                  <p
                    className="mt-1 text-[11px] leading-[1.5]"
                    style={{ color: 'var(--color-faint, #9CA3AF)' }}
                  >
                    {perm.permissionDescription}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────
   Loading skeleton — paper-warm bars on the editorial palette
   ──────────────────────────────────────────────────────────────────── */

function DrawerSkeleton() {
  return (
    <div className="space-y-7 animate-pulse">
      {/* Identity skeleton */}
      <div>
        <div className="mb-3 flex items-center gap-2">
          <div className="h-5 w-24 rounded-full bg-[color:var(--color-rule-soft,#F0EBDD)]" />
          <div className="h-3 w-32 rounded bg-[color:var(--color-rule-soft,#F0EBDD)]" />
        </div>
        <div className="h-6 w-3/4 rounded bg-[color:var(--color-rule-soft,#F0EBDD)]" />
      </div>

      {/* AI assessment skeleton */}
      <div
        className="rounded-[10px] border p-5 space-y-3"
        style={{
          background: 'var(--color-paper-warm, #FBF8F2)',
          borderColor: 'var(--color-rule-soft, #F0EBDD)',
        }}
      >
        <div className="h-4 w-32 rounded bg-[color:var(--color-rule,#E6E1D5)]" />
        <div className="h-8 w-full rounded bg-[color:var(--color-rule,#E6E1D5)]" />
        <div className="h-3 w-full rounded bg-[color:var(--color-rule,#E6E1D5)]" />
        <div className="h-3 w-3/4 rounded bg-[color:var(--color-rule,#E6E1D5)]" />
      </div>

      {/* Permissions skeleton */}
      <div
        className="overflow-hidden rounded-[10px] border"
        style={{ borderColor: 'var(--color-rule-soft, #F0EBDD)' }}
      >
        <div className="px-4 py-3">
          <div className="h-4 w-40 rounded bg-[color:var(--color-rule-soft,#F0EBDD)]" />
        </div>
        <div className="space-y-3 p-4">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="space-y-1.5">
              <div className="h-3 w-40 rounded bg-[color:var(--color-rule-soft,#F0EBDD)]" />
              <div className="h-3 w-24 rounded bg-[color:var(--color-rule-soft,#F0EBDD)]" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
